import {useEffect, useState, useCallback} from 'react';
import {GoogleMap, Marker, Polygon} from '@react-google-maps/api';
import {Navigation} from 'lucide-react';
import {Report, Zone} from '../types';
import {Button} from './ui/button';
import {useToast} from '../contexts/ToastContext';
import {useAuth} from '../hooks/useAuth';

interface MapViewProps {
  reports: Report[];
  onReportClick: (report: Report) => void;
  selectedReportId?: string;
  onMapClick?: (event: React.MouseEvent<HTMLDivElement>, lat?: number, lng?: number) => void;
  onReportDetailClick?: (report: Report) => void;
  zones?: Zone[];
  onZoneClick?: (zone: Zone) => void;
  selectedZoneId?: string;
  hoveredZoneId?: string;
  onZoneHover?: (zoneId: string | undefined) => void;
  onZoneDetailClick?: (zone: Zone) => void;
  showZones?: boolean;
  currentView?: 'reports' | 'zones'; // Nouveau prop pour déterminer la vue active
  centerOnLocation?: {lat: number; lng: number} | null; // Pour centrer sur une position spécifique
  zoomToBounds?: {north: number; south: number; east: number; west: number} | null; // Pour zoomer sur une zone
  showLocationButton?: boolean; // Pour contrôler l'affichage du bouton Ma position
  onMapChange?: (center: {lat: number; lng: number}, zoom: number) => void; // Callback quand la carte bouge
  initialUserLocation?: {latitude: number; longitude: number}; // Position initiale de l'utilisateur
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

// Centre par défaut : Forêt de Compiègne
const defaultCenter = {
  lat: 49.4144,
  lng: 2.8322,
};

const getMapOptions = (currentView?: 'reports' | 'zones') => ({
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: true,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{visibility: 'off'}],
    },
  ],
  draggableCursor: 'crosshair',
  gestureHandling: currentView === 'reports' || currentView === 'zones' ? 'greedy' : 'cooperative',
});

export default function MapView({
  reports,
  onReportClick,
  selectedReportId,
  onMapClick,
  onReportDetailClick,
  zones = [],
  onZoneClick,
  selectedZoneId,
  hoveredZoneId,
  onZoneHover,
  currentView = 'reports',
  centerOnLocation,
  zoomToBounds,
  showLocationButton = true,
  onMapChange,
  initialUserLocation
}: MapViewProps) {
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(
    initialUserLocation ? {lat: initialUserLocation.latitude, lng: initialUserLocation.longitude} : null
  );
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationAttempted, setLocationAttempted] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const toast = useToast();
  const {user} = useAuth();

  // Fonction pour créer une icône de pin avec le nouveau design
  const createPinIcon = (color: string, isSelected: boolean = false) => {
    const size = isSelected ? 67 : 52; // Taille réduite de moitié
    return {
      url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 24 28" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="11" r="3" stroke="white" stroke-width="1.5" fill="${color}"/>
          <path d="M21 10.8889C21 15.7981 15.375 22 12 22C8.625 22 3 15.7981 3 10.8889C3 5.97969 7.02944 2 12 2C16.9706 2 21 5.97969 21 10.8889Z" stroke="white" stroke-width="1.5" fill="${color}"/>
          <text x="12" y="14" text-anchor="middle" font-size="10" fill="white">🐗</text>
        </svg>
      `),
      scaledSize: typeof google !== 'undefined' && google.maps && google.maps.Size ? new google.maps.Size(size, size) : undefined,
      anchor: typeof google !== 'undefined' && google.maps && google.maps.Point ? new google.maps.Point(size / 2, size - 6) : undefined,
    };
  };

  // Tenter la géolocalisation au démarrage avec fallback IP
  useEffect(() => {
    if (!locationAttempted) {
      setLocationAttempted(true);

      // Ne pas définir la position par défaut si on a déjà une position initiale
      if (!initialUserLocation) {
        setUserLocation({
          lat: defaultCenter.lat,
          lng: defaultCenter.lng,
        });
      }

      // Fonction pour obtenir la position via IP (fallback)
      const getLocationByIP = async () => {
        try {
          const response = await fetch('https://ipapi.co/json/');
          const data = await response.json();
          if (data.latitude && data.longitude) {
            setUserLocation({
              lat: data.latitude,
              lng: data.longitude,
            });
          }
        } catch (error) {
        }
      };

      // Ensuite, tenter la géolocalisation native
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          async () => {
            // Si la géoloc native échoue, essayer via IP
            await getLocationByIP();
          },
          {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 300000
          }
        );
      } else {
        // Si pas de géoloc native, essayer directement via IP
        getLocationByIP();
      }
    }
  }, [locationAttempted]);

  // Fonction pour tenter la géolocalisation
  const retryGeolocation = useCallback(() => {
    if (navigator.geolocation && !isGettingLocation) {
      setIsGettingLocation(true);
      const options = {
        enableHighAccuracy: true, // Essayer la précision maximale
        timeout: 15000, // Timeout plus long pour retry manuel
        maximumAge: 0 // Forcer une nouvelle localisation
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          // Location found successfully
          setIsGettingLocation(false);
          toast.success('Position trouvée', 'Votre position a été détectée');
        },
        (error) => {
          console.error('❌ Erreur de géolocalisation:', error);
          setIsGettingLocation(false);

          // Ne pas afficher d'erreur pour kCLErrorLocationUnknown (simulateur/certains navigateurs)
          if (error.code !== 2) { // 2 = POSITION_UNAVAILABLE
            switch (error.code) {
              case error.PERMISSION_DENIED:
                toast.warning('Géolocalisation refusée', 'Vérifiez les permissions de votre navigateur');
                break;
              case error.TIMEOUT:
                toast.warning('Localisation lente', 'Essayez de vous déplacer vers une fenêtre');
                break;
              default:
                toast.warning('Erreur de localisation', 'Impossible d\'obtenir votre position');
                break;
            }
          }
        },
        options
      );
    }
  }, [toast, isGettingLocation]);

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);

    // Ajouter des listeners pour capturer les changements
    if (onMapChange) {
      mapInstance.addListener('idle', () => {
        const center = mapInstance.getCenter();
        const zoom = mapInstance.getZoom();
        if (center && zoom !== undefined) {
          onMapChange({lat: center.lat(), lng: center.lng()}, zoom);
        }
      });
    }
  }, [onMapChange]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Effet pour centrer la carte sur une location spécifique 
  useEffect(() => {
    if (map) {
      if (zoomToBounds) {
        // Si on a des bounds (pour une zone), utiliser fitBounds
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(zoomToBounds.south, zoomToBounds.west),
          new google.maps.LatLng(zoomToBounds.north, zoomToBounds.east)
        );
        map.fitBounds(bounds, {
          top: 100,
          right: 100,
          bottom: 100,
          left: 100
        });
      } else if (centerOnLocation) {
        // Sinon centrer sur la location (pour un signalement)
        map.panTo(centerOnLocation);
        map.setZoom(15); // Zoom plus proche pour voir le signalement
      }
    }
  }, [map, centerOnLocation, zoomToBounds]);

  const handleMapClick = (e: any) => {
    if (onMapClick && e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Créer un événement synthétique valide
      const syntheticEvent = {
        target: {
          closest: () => null
        },
        currentTarget: {
          closest: () => null
        },
        preventDefault: () => { },
        stopPropagation: () => { }
      } as unknown as React.MouseEvent<HTMLDivElement>;

      onMapClick(syntheticEvent, lat, lng);
    }
  };

  const handleMarkerClick = (report: Report) => {
    // Ouvrir directement la page de détails au lieu de l'InfoWindow
    if (onReportDetailClick) {
      onReportDetailClick(report);
    } else {
      onReportClick(report);
    }
  };

  const getZoneColor = () => {
    // Couleur par défaut pour toutes les zones
    return '#10b981';
  };

  return (
    <div className="relative w-full h-full lg:rounded-2xl overflow-hidden lg:shadow-lg lg:border lg:border-gray-100/50">
      {/* Note: Nous utilisons délibérément google.maps.Marker (deprecated) car AdvancedMarkerElement 
            nécessite une migration complexe et l'ancienne API fonctionne parfaitement */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={userLocation || (initialUserLocation ? {lat: initialUserLocation.latitude, lng: initialUserLocation.longitude} : defaultCenter)}
        zoom={13}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={getMapOptions(currentView)}
      >
        {/* Bouton de géolocalisation - Placé à l'intérieur de la carte */}
        {showLocationButton && (
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 1
          }}>
            <Button
              onClick={retryGeolocation}
              size="sm"
              disabled={isGettingLocation}
              className="bg-blue-500 hover:bg-blue-600 text-white shadow-xl border-0 rounded-xl"
            >
              <Navigation className={`w-4 h-4 mr-2 ${isGettingLocation ? 'animate-spin' : ''}`} />
              {isGettingLocation ? 'Localisation...' : 'Ma position'}
            </Button>
          </div>
        )}

        {/* Position de l'utilisateur */}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={{
              url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
                  <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="12" fill="#3b82f6" stroke="white" stroke-width="3" opacity="0.3"/>
                    <circle cx="16" cy="16" r="8" fill="#3b82f6" stroke="white" stroke-width="2"/>
                  </svg>
                `),
              scaledSize: typeof google !== 'undefined' && google.maps && google.maps.Size ? new google.maps.Size(32, 32) : undefined,
              anchor: typeof google !== 'undefined' && google.maps && google.maps.Point ? new google.maps.Point(16, 16) : undefined,
            }}
            title="Votre position - Cliquez pour signaler"
            onClick={(e) => {
              if (onMapClick && currentView === 'reports') {
                // Passer les coordonnées de la position utilisateur
                onMapClick(e as any, userLocation.lat, userLocation.lng);
              }
            }}
            cursor={currentView === 'reports' ? 'pointer' : 'default'}
          />
        )}

        {/* Zones - Affichage conditionnel selon la vue */}
        {zones.map((zone) => {
          // Calculer l'opacité selon la vue active
          const isZoneView = currentView === 'zones';
          const baseOpacity = isZoneView ? 0.3 : 0.15; // Plus visible en vue zones, plus discrète en vue signalements
          const fillOpacity = selectedZoneId === zone.id ? baseOpacity + 0.2 :
            hoveredZoneId === zone.id ? baseOpacity + 0.1 :
              baseOpacity;
          const strokeOpacity = isZoneView ? 0.8 : 0.4;

          return (
            <Polygon
              key={zone.id}
              paths={zone.coordinates.map(coord => ({lat: coord.latitude, lng: coord.longitude}))}
              options={{
                fillColor: getZoneColor(),
                fillOpacity,
                strokeColor: getZoneColor(),
                strokeOpacity,
                strokeWeight: selectedZoneId === zone.id ? 3 : (isZoneView ? 2 : 1),
                clickable: isZoneView, // Cliquable seulement en vue zones
              }}
              onClick={isZoneView ? () => onZoneClick?.(zone) : undefined}
              onMouseOver={isZoneView ? () => onZoneHover?.(zone.id) : undefined}
              onMouseOut={isZoneView ? () => onZoneHover?.(undefined) : undefined}
            />
          );
        })}

        {/* Marqueurs de signalements */}
        {reports.map((report) => {
          // Déterminer la couleur : bleu si c'est moi, vert sinon
          const isMyReport = report.userId === user?.id;
          let color = isMyReport ? '#3b82f6' : '#10b981';

          // Réduire l'opacité en vue zones
          if (currentView === 'zones') {
            color = isMyReport ? '#3b82f680' : '#10b98180'; // Ajout de l'alpha 80 (50% d'opacité)
          }

          const isSelected = selectedReportId === report.id;

          return (
            <Marker
              key={report.id}
              position={{lat: report.latitude, lng: report.longitude}}
              icon={createPinIcon(color, isSelected)}
              onClick={() => handleMarkerClick(report)}
            />
          );
        })}

      </GoogleMap>

      {/* Légende */}
      <div className="absolute top-4 left-4 lg:bottom-4 lg:left-4 lg:top-auto bg-white/95 backdrop-blur-md rounded-xl p-3 shadow-lg border border-gray-100/50">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full" />
            <span>Votre position</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-base">🐗</span>
            <span>Signalements</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-2">
            <span>+</span>
            <span>Cliquer sur la carte pour signaler</span>
          </div>
        </div>
      </div>
    </div>
  );
}